import { deriveCouponStatus } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { EmptyState, Icon } from '@mypoz/ui';

import { CouponCard } from '@/features/coupons/components/CouponCard.js';
import { buttonClass } from '@/lib/styles.js';
import { useCoupons } from '@/features/coupons/index.js';

/**
 * The coupon list (MYP-71) — every promo code issued to the customer's account, valid, used or
 * expired alike. `get_onlinecode` doesn't filter itself (see the ticket and `deriveCouponStatus`
 * in @mypoz/shared), so this screen has to render all three states rather than assuming a clean
 * "my active coupons" list arrives already narrowed.
 *
 * Wired to `get_onlinecode` since MYP-73. Every coupon the account has ever been issued arrives,
 * and on the test account all 78 of them are expired — an all-grey list is the honest rendering of
 * that, not a bug.
 */
export function Coupons() {
  const { t } = useTranslation();
  const { coupons, status, reload } = useCoupons();
  // One clock for every card's status derivation, so nothing in this render disagrees with
  // itself about what "now" is — same reasoning as Packages' own `now`.
  const now = new Date();

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-ink">{t('coupons.list.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('coupons.list.subtitle')}</p>
      </div>

      {/*
        The same four-state ladder as Packages and Orders, in the same order: the request first,
        then the answer. "You have no coupons" is the wrong thing to tell someone whose request
        never came back.
      */}
      {status === 'loading' ? (
        <EmptyState icon="hourglass_empty" title={t('coupons.list.loading')} />
      ) : status === 'error' ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="cloud_off"
            title={t('coupons.list.failed')}
            description={t('coupons.list.failedBody')}
          />
          <button type="button" onClick={reload} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('coupons.list.retry')}
          </button>
        </div>
      ) : coupons.length === 0 ? (
        <EmptyState
          icon="redeem"
          title={t('coupons.list.empty')}
          description={t('coupons.list.emptyBody')}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {coupons.map((coupon) => (
              <CouponCard
                key={coupon.id}
                coupon={coupon}
                status={deriveCouponStatus(coupon, now)}
              />
            ))}
          </div>

          <p className="mt-4 text-sm text-muted">{t('coupons.list.footnote')}</p>
        </>
      )}
    </>
  );
}
